/**
 * The Apollo-context contract between an operation and the shared connection store's
 * `accountMiddleware` (`@vp/vue-components`, `lib/connection/store.ts`).
 *
 * That middleware refuses to put an account-scoped operation on the wire without an
 * `X-VP-Account-Id` header, because a request that arrives without one is scoped by the backend
 * to `current_user.default_account` — an arbitrary tenant — which surfaces as "not found" for a
 * record the operator is looking straight at.
 *
 * Two escapes, and only two:
 *
 * - `ACCOUNT_AGNOSTIC` for the operations that genuinely have no account: the pre-login handful on
 *   the tenant schemas — login, the config read, account discovery, webauthn, the staff probe — and
 *   most of the internal (VP-staff) surface, which addresses platform registries and globally
 *   unique records rather than one tenant's rows (`Internal/accountScope.spec.ts` pins which).
 *
 *   It is spelled at each call site rather than as a list here, so the exemption is reviewed next
 *   to the document it exempts. An operation that says nothing is account-scoped — new work fails
 *   closed. AppSchema's `currentUser` is the exemption people expect and do not get: it selects
 *   `accountConfig`. The internal schema's selects only the operator, so that one is exempt.
 * - `forAccount(id)` for a caller that knows which account it means and cannot rely on the store's
 *   ref: the VenuePilot-staff screens under `/s`, which administer an account the operator is not
 *   a member of, and the staff reads whose own filter names an account.
 *
 * This lives here rather than in `@vp/vue-components` because the account-agnostic operations are
 * defined in this package, and the dependency runs vue-components → vp-common.
 */

/** Context keys, so the middleware and the call sites cannot drift on a string literal. */
export type AccountContext = {
  vpAccountAgnostic?: boolean;
  vpAccountId?: number;
};

/** Spread into an operation's `context` to declare it needs no account. */
export const ACCOUNT_AGNOSTIC: AccountContext = { vpAccountAgnostic: true };

/** Spread into an operation's `context` to name the account it operates on, overriding the store. */
export const forAccount = (accountId: number): AccountContext => ({ vpAccountId: accountId });

/**
 * Raised by `accountMiddleware` instead of sending an account-scoped operation unscoped.
 *
 * It reaches a caller as `(error as ApolloError).networkError`, never as the error itself: Apollo
 * wraps anything a link errors with before `client.query`/`client.mutate` rejects. So an
 * `err instanceof MissingAccountError` check at a call site is a branch that never runs — match on
 * `vpCode` via `networkError`, which is also what groups these in telemetry.
 */
export class MissingAccountError extends Error {
  readonly vpCode = 'NO_ACCOUNT_CLIENT';
  readonly operationName: string;

  constructor(operationName: string) {
    super(`Refusing to send "${operationName}" without an account: no account is selected.`);
    this.name = 'MissingAccountError';
    this.operationName = operationName;
  }
}
